'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { trackFormSubmit } from '@/lib/analytics';
import { getStripe } from '@/lib/stripe/client';

interface TrialFormData {
  fullName: string;
  email: string;
  phone: string;
  companyName: string;
  city: string;
  state: string;
  zipCode: string;
  companySize: 'solo' | 'small' | 'medium' | 'large' | '';
  currentQuotingMethod: 'manual' | 'spreadsheet' | 'software' | 'other' | '';
}

export default function FreeTrialForm() {
  const router = useRouter();
  const [formData, setFormData] = useState<TrialFormData>({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    city: '',
    state: '',
    zipCode: '',
    companySize: '',
    currentQuotingMethod: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Track page view on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Free Trial Sign Up',
        page_location: window.location.href,
      });
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Track trial signup conversion
      trackFormSubmit('free_trial_signup', {
        company_size: formData.companySize,
        quoting_method: formData.currentQuotingMethod,
        state: formData.state,
      });

      // Store trial data in localStorage for later use
      localStorage.setItem('trialUserData', JSON.stringify(formData));

      // Create Stripe checkout session for $1 trial
      const response = await fetch('/api/trial-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          fullName: formData.fullName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { sessionId } = await response.json();

      // Redirect to Stripe Checkout
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }
    } catch (err: any) {
      console.error('Trial signup error:', err);
      setError(err.message || 'Failed to start trial. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-custom py-20 lg:py-32">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-blur">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse-glow" />
            <span className="text-xs uppercase tracking-widest font-semibold text-primary">
              $1 for 14-Day Trial
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-6">
            Start Your <span className="text-gradient">Trial</span>
          </h1>
          
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Join 1,200+ contractors who've transformed their quoting process with market-driven pricing intelligence.
          </p>

          {/* Trust Elements */}
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success" />
              <span className="text-muted-foreground">Secure payment required</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success" />
              <span className="text-muted-foreground">$1 for 14 days</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success" />
              <span className="text-muted-foreground">Cancel anytime</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-card border border-border rounded-2xl shadow-xl p-8 md:p-12 animate-fade-in-blur delay-300">
          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div>
              <h2 className="text-2xl font-heading font-bold mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                  1
                </div>
                Personal Information
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-semibold text-foreground mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="john@hvaccompany.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div>
              <h2 className="text-2xl font-heading font-bold mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                  2
                </div>
                Company Information
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="companyName" className="block text-sm font-semibold text-foreground mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="Smith HVAC Services"
                  />
                </div>
                <div>
                  <label htmlFor="city" className="block text-sm font-semibold text-foreground mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="Phoenix"
                  />
                </div>
                <div>
                  <label htmlFor="state" className="block text-sm font-semibold text-foreground mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="AZ"
                  />
                </div>
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-semibold text-foreground mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="85001"
                  />
                </div>
                <div>
                  <label htmlFor="companySize" className="block text-sm font-semibold text-foreground mb-2">
                    Company Size *
                  </label>
                  <select
                    id="companySize"
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                  >
                    <option value="">Select size</option>
                    <option value="solo">Solo (Just me)</option>
                    <option value="small">Small (2-10 employees)</option>
                    <option value="medium">Medium (11-50 employees)</option>
                    <option value="large">Large (50+ employees)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Current Process */}
            <div>
              <h2 className="text-2xl font-heading font-bold mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                  3
                </div>
                Current Quoting Process
              </h2>
              <div>
                <label htmlFor="currentQuotingMethod" className="block text-sm font-semibold text-foreground mb-2">
                  How do you currently create quotes? *
                </label>
                <select
                  id="currentQuotingMethod"
                  name="currentQuotingMethod"
                  value={formData.currentQuotingMethod}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                >
                  <option value="">Select method</option>
                  <option value="manual">Manual calculations</option>
                  <option value="spreadsheet">Spreadsheets (Excel/Google Sheets)</option>
                  <option value="software">Other quoting software</option>
                  <option value="other">Other method</option>
                </select>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Continue to Payment
                  <Icon name="ArrowRightIcon" size={20} variant="outline" />
                </>
              )}
            </button>

            <p className="text-center text-sm text-muted-foreground">
              By continuing, you agree to our{' '}
              <Link href="#" className="text-primary hover:underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="#" className="text-primary hover:underline">
                Privacy Policy
              </Link>
            </p>
          </form>
        </div>

        {/* Already have an account */}
        <div className="text-center mt-8">
          <p className="text-muted-foreground">
            Already have an account?{' '}
            <Link href="/login" className="text-primary font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}